import { useEffect, useMemo, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import cn from 'classnames'
import {
   Button,
   Checkbox,
   ContentCard,
   Grid,
   MultiSelect,
   Stack,
   TextInput,
} from '@forwardflow/ui-v2'
import {
   ArrowLeftMdIcon,
   BullsEyeArrowIcon,
   CheckCircle02Icon,
   ChevronRightIcon,
   PondIcon,
   ZapIcon,
} from '@forwardflow/icons'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import {
   useGetAllLeadSources,
   useGetAllRecommendedCampaign,
} from '@forwardflow/features/campaign'
import { IFilterCriteria } from '@forwardflow/features/campaign-filter/interfaces/campaign-filter.types'
import { IAllLeadSources } from '@forwardflow/types'
import { formatNumber } from '@forwardflow/helpers/utils/formatNumber.ts'
import {
   DEMO_LEAD_SOURCES,
   DEMO_PONDS,
   DEMO_STAGES,
   DEMO_TAGS,
   isFreeFlow,
   useDevTools,
} from '../dev/devTools'
import {
   buildStarterCampaigns,
   compileDescription,
   customCampaign,
   IStarterCampaign,
   nameFor,
   TCampaignKind,
   TLeadTypeId,
} from './first-campaign'
import { useLeadCount } from './first-campaign/useLeadCount'

const LEAD_TYPES: { id: TLeadTypeId; label: string }[] = [
   { id: 'buyer', label: 'Buyers' },
   { id: 'seller', label: 'Sellers' },
   { id: 'rental', label: 'Renters' },
]

const KINDS: { id: TCampaignKind; label: string; hint: string }[] = [
   { id: 'new-lead', label: 'New leads', hint: 'Call within minutes of arrival' },
   { id: 'revive', label: 'Older leads', hint: 'Work the backlog a few a day' },
]

const CardIcon = ({ template }: { template: IStarterCampaign['template'] }) => {
   const cls = 'w-10 h-10 rounded-[8px] border border-white/10 flex items-center justify-center shrink-0'
   if (template === 'revive')
      return (
         <span className={cn(cls, 'bg-[#FF8904]/10 text-[#FF8904]')}>
            <BullsEyeArrowIcon width={20} height={20} />
         </span>
      )
   if (template === 'pond')
      return (
         <span className={cn(cls, 'bg-[#00D492]/10 text-[#00D492]')}>
            <PondIcon width={20} height={20} />
         </span>
      )
   return (
      <span className={cn(cls, 'bg-[#9F7BFF]/10 text-[#A684FF]')}>
         <ZapIcon width={20} height={20} />
      </span>
   )
}

const StarterCard = ({
   campaign,
   selected,
   onPick,
}: {
   campaign: IStarterCampaign
   selected: boolean
   onPick: (c: IStarterCampaign, count?: number) => void
}) => {
   const { count, loading } = useLeadCount(campaign.criteria, campaign.knownCount)
   return (
      <button
         type="button"
         onClick={() => onPick(campaign, count)}
         data-testid={`starter-${campaign.template}`}
         className={cn(
            'text-left rounded-2xl border p-5 transition-all flex gap-4 w-full',
            selected
               ? 'border-[#9F7BFF] bg-[rgba(159,123,255,0.10)]'
               : 'border-[#3f3e47] bg-white/[0.02] hover:border-white/30'
         )}
      >
         <CardIcon template={campaign.template} />
         <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
               <p className="text-white font-semibold">{campaign.title}</p>
               {campaign.recommended && (
                  <span className="text-[10px] uppercase tracking-wide font-semibold rounded-full bg-[#9F7BFF]/25 text-[#C9B8FF] px-2 py-0.5">
                     Recommended
                  </span>
               )}
            </div>
            <p className="text-[#99A1AF] text-sm mt-1">{campaign.description}</p>
            <p className="text-white/80 text-sm mt-2">
               {loading && count === undefined ? (
                  <span className="inline-block h-4 w-24 rounded bg-white/10 animate-pulse align-middle" />
               ) : count !== undefined ? (
                  <>
                     <span className="font-semibold text-white">{formatNumber(count)}</span>{' '}
                     {count === 1 ? 'lead' : 'leads'} match today
                  </>
               ) : (
                  'Counting your leads'
               )}
            </p>
         </div>
         <span className={cn('self-center transition-opacity', selected ? 'opacity-100' : 'opacity-0')}>
            <CheckCircle02Icon width={22} height={22} color="#A684FF" />
         </span>
      </button>
   )
}

/**
 * Step 5: which leads to call first.
 *
 * Three starter campaigns built from the account (recommended source, revive,
 * pond), each with its live lead count. One tap picks a card and moves on.
 * Below them, "describe who to call" compiles plain words into the same
 * filters, with chips and a count, and an advanced panel exposes the filters
 * directly. Whatever is picked is stored in `campaignConfiguration` and
 * created, with numbers and voice, on the last step.
 */
export const FirstCampaign = () => {
   const {
      onboardingData,
      sources: storeSources,
      stages: storeStages,
      tags: storeTags,
      ponds: storePonds,
      getNextStep,
      getPreviousStep,
      setCampaignConfiguration,
   } = useOnboardingStoreV2()
   const freeFlow = isFreeFlow()
   const saved = onboardingData.campaignConfiguration

   const { data: recommended } = useGetAllRecommendedCampaign()
   const { data: leadSources, isLoading: sourcesLoading } = useGetAllLeadSources({
      page: 1,
      limit: 50,
      search: '',
   })

   const sourceRows = useMemo(() => {
      const rows = ((leadSources || []) as IAllLeadSources[]).filter((s) => s.leadSource)
      if (rows.length) return rows
      // Sources the sync has seen but no campaign stats yet.
      const fromStore = (storeSources || []).filter(Boolean).map((leadSource) => ({ leadSource }))
      if (fromStore.length) return fromStore
      return freeFlow ? DEMO_LEAD_SOURCES : []
   }, [leadSources, storeSources, freeFlow])
   const pondRows = useMemo(
      () => (storePonds?.length ? storePonds : freeFlow ? DEMO_PONDS : []),
      [storePonds, freeFlow]
   )
   const stageRows = useMemo(
      () =>
         storeStages?.length
            ? storeStages.map((s) => ({ id: String(s.id), name: s.name }))
            : freeFlow
              ? DEMO_STAGES.map((s) => ({ id: s.id, name: s.name }))
              : [],
      [storeStages, freeFlow]
   )
   const tagRows = useMemo(
      () => (storeTags?.length ? storeTags : freeFlow ? DEMO_TAGS.map((t) => t.name) : []),
      [storeTags, freeFlow]
   )

   const cards = useMemo(
      () =>
         buildStarterCampaigns({
            recommended: ((recommended || []) as Array<{ title?: string; source?: string }>) ?? [],
            sources: sourceRows,
            ponds: pondRows,
         }),
      [recommended, sourceRows, pondRows]
   )

   const [selectedId, setSelectedId] = useState<string | null>(() =>
      saved?.campaign_name ? `saved:${saved.campaign_name}` : null
   )

   /* ---------- Describe / advanced ---------- */

   const [text, setText] = useState('')
   const [advanced, setAdvanced] = useState(false)
   const [draft, setDraft] = useState<{
      criteria: IFilterCriteria[]
      campaignType: TCampaignKind[]
      leadTypes: TLeadTypeId[]
      understood: string[]
      nothingMatched: boolean
   } | null>(null)

   const vocab = useMemo(
      () => ({
         sources: sourceRows.map((s) => s.leadSource),
         stages: stageRows,
         tags: tagRows,
         ponds: pondRows,
      }),
      [sourceRows, stageRows, tagRows, pondRows]
   )

   // Compile as the customer types (debounced).
   const typing = useRef<ReturnType<typeof setTimeout> | null>(null)
   useEffect(() => {
      if (typing.current) clearTimeout(typing.current)
      if (!text.trim()) {
         if (!advanced) setDraft(null)
         return
      }
      typing.current = setTimeout(() => setDraft(compileDescription(text, vocab)), 350)
      return () => {
         if (typing.current) clearTimeout(typing.current)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [text, vocab])

   const openAdvanced = () => {
      setAdvanced(true)
      if (!draft) {
         setDraft({
            criteria: [],
            campaignType: ['new-lead'],
            leadTypes: ['buyer', 'seller'],
            understood: [],
            nothingMatched: false,
         })
      }
   }

   const valuesFor = (field: IFilterCriteria['field'], operator: IFilterCriteria['operator']) =>
      (draft?.criteria.find((c) => c.field === field && c.operator === operator)?.value || []).map(
         String
      )
   const setValues = (
      field: IFilterCriteria['field'],
      operator: IFilterCriteria['operator'],
      values: string[]
   ) => {
      if (!draft) return
      const rest = draft.criteria.filter((c) => !(c.field === field && c.operator === operator))
      const typed: Array<string | number> =
         field === 'assigned_pond_id' ? values.map(Number) : values
      setDraft({
         ...draft,
         criteria: values.length ? [...rest, { field, logic: 'AND', value: typed, operator }] : rest,
         understood: draft.understood,
      })
   }
   const toggleLeadType = (id: TLeadTypeId) => {
      if (!draft) return
      const has = draft.leadTypes.includes(id)
      if (has && draft.leadTypes.length === 1) return
      setDraft({
         ...draft,
         leadTypes: has ? draft.leadTypes.filter((t) => t !== id) : [...draft.leadTypes, id],
      })
   }

   const { count: draftCount, loading: draftLoading } = useLeadCount(
      draft ? draft.criteria : null
   )

   /* ---------- Pick ---------- */

   const save = (campaign: IStarterCampaign) => {
      setCampaignConfiguration({
         campaign_name: campaign.campaignName,
         source: campaign.source || '',
         campaign_filters: { eligible_criteria: campaign.criteria },
         campaign_type: campaign.campaignType,
         lead_type_filter: campaign.leadTypes,
         first_to_contact: { buffer_time: campaign.bufferTime },
      })
   }

   const pickCard = (campaign: IStarterCampaign) => {
      setSelectedId(campaign.id)
      save(campaign)
      getNextStep()
   }

   const pickDraft = () => {
      if (!draft) return
      const name = text.trim() ? nameFor(draft) : 'Custom campaign'
      const campaign = customCampaign(draft, name)
      setSelectedId('custom')
      save(campaign)
      getNextStep()
   }

   // Dev tools (hidden bar): the recommended card.
   useDevTools({
      fill: () => setText('Zillow buyers'),
      skip: () => {
         if (cards[0]) pickCard(cards[0])
         else getNextStep()
         return true
      },
   })

   const isLoadingCards = sourcesLoading && !cards.length
   const stageOptions = stageRows.map((s) => ({ value: s.id, label: s.name }))
   const tagOptions = tagRows.map((t) => ({ value: t, label: t }))
   const sourceOptions = sourceRows.map((s) => ({ value: s.leadSource, label: s.leadSource }))
   const pondOptions = pondRows.map((p) => ({ value: String(p.pondId), label: p.pondName }))
   const draftReady = Boolean(draft) && !draft?.nothingMatched && draftCount !== 0

   return (
      <Stack gap={24}>
         <ContentCard
            className="space-y-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
         >
            <Stack gap={4}>
               <p className="text-white font-semibold text-lg">Who should we call first?</p>
               <p className="text-[#99A1AF] text-sm font-medium max-w-[640px]">
                  Built from your Follow Up Boss account. Tap one to use it;
                  your stop rules apply to every campaign.
               </p>
            </Stack>

            {isLoadingCards ? (
               <div className="space-y-3">
                  {[0, 1, 2].map((i) => (
                     <div key={i} className="h-24 rounded-2xl bg-white/10 animate-pulse" />
                  ))}
               </div>
            ) : cards.length ? (
               <div className="space-y-3" data-testid="starter-cards">
                  {cards.map((c) => (
                     <StarterCard
                        key={c.id}
                        campaign={c}
                        selected={
                           selectedId === c.id || selectedId === `saved:${c.campaignName}`
                        }
                        onPick={pickCard}
                     />
                  ))}
               </div>
            ) : (
               <p className="text-white/50 text-sm">
                  We're still pulling your lead sources from Follow Up Boss.
                  Describe who to call below, or come back in a minute.
               </p>
            )}
         </ContentCard>

         <ContentCard
            className="space-y-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.15 }}
         >
            <TextInput
               label="Or describe who to call in your own words"
               placeholder='e.g. "Zillow buyers from the last 30 days, not in my Sphere"'
               value={text}
               onChange={(e) => setText(e.currentTarget.value)}
               data-testid="describe-leads"
            />

            {draft && (
               <div className="space-y-3" data-testid="draft-summary">
                  {draft.nothingMatched && text.trim() && !advanced ? (
                     <p className="text-[#FF8904] text-sm">
                        We couldn't match that to a source, stage, tag or pond in
                        your Follow Up Boss. Try the names you use there, or open
                        the filters.
                     </p>
                  ) : (
                     <div className="flex flex-wrap items-center gap-2">
                        {draft.understood.map((u) => (
                           <span
                              key={u}
                              className="rounded-[8px] bg-white/15 px-3 py-1 text-sm text-white"
                           >
                              {u}
                           </span>
                        ))}
                        {advanced && draft.understood.length === 0 && (
                           <span className="text-white/50 text-sm">
                              {draft.criteria.length ? 'Custom filters' : 'Every lead not on a stop stage'}
                           </span>
                        )}
                        <span className="text-white/70 text-sm ml-auto">
                           {draftLoading && draftCount === undefined ? (
                              'Counting'
                           ) : draftCount !== undefined ? (
                              <>
                                 <b className="text-white">{formatNumber(draftCount)}</b>{' '}
                                 {draftCount === 1 ? 'lead' : 'leads'} match
                              </>
                           ) : null}
                        </span>
                     </div>
                  )}
               </div>
            )}

            {!advanced ? (
               <button
                  type="button"
                  className="text-[#A684FF] text-sm font-medium hover:underline"
                  onClick={openAdvanced}
               >
                  Advanced filters
               </button>
            ) : (
               draft && (
                  <div className="space-y-4 border-t border-white/10 pt-4" data-testid="advanced-filters">
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <MultiSelect
                           label="From these sources"
                           data={sourceOptions}
                           value={valuesFor('source', 'CONTAINS_ANY')}
                           onChange={(v) => setValues('source', 'CONTAINS_ANY', v)}
                           searchable
                           comboboxProps={{ withinPortal: true }}
                        />
                        <MultiSelect
                           label="In these ponds"
                           data={pondOptions}
                           value={valuesFor('assigned_pond_id', 'CONTAINS_ANY')}
                           onChange={(v) => setValues('assigned_pond_id', 'CONTAINS_ANY', v)}
                           searchable
                           comboboxProps={{ withinPortal: true }}
                        />
                        <MultiSelect
                           label="In these stages"
                           data={stageOptions}
                           value={valuesFor('stage', 'CONTAINS_ANY')}
                           onChange={(v) => setValues('stage', 'CONTAINS_ANY', v)}
                           searchable
                           comboboxProps={{ withinPortal: true }}
                        />
                        <MultiSelect
                           label="Not in these stages"
                           data={stageOptions}
                           value={valuesFor('stage', 'NOT_CONTAINS_ANY')}
                           onChange={(v) => setValues('stage', 'NOT_CONTAINS_ANY', v)}
                           searchable
                           comboboxProps={{ withinPortal: true }}
                        />
                        <MultiSelect
                           label="With any of these tags"
                           data={tagOptions}
                           value={valuesFor('tags', 'CONTAINS_ANY')}
                           onChange={(v) => setValues('tags', 'CONTAINS_ANY', v)}
                           searchable
                           comboboxProps={{ withinPortal: true }}
                        />
                        <MultiSelect
                           label="Without these tags"
                           data={tagOptions}
                           value={valuesFor('tags', 'NOT_CONTAINS_ANY')}
                           onChange={(v) => setValues('tags', 'NOT_CONTAINS_ANY', v)}
                           searchable
                           comboboxProps={{ withinPortal: true }}
                        />
                     </div>
                     <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
                        <div className="flex items-center gap-4">
                           <span className="text-white/60 text-sm">Lead types</span>
                           {LEAD_TYPES.map((t) => (
                              <label key={t.id} className="flex items-center gap-2 text-sm text-white cursor-pointer">
                                 <Checkbox
                                    size="xs"
                                    checked={draft.leadTypes.includes(t.id)}
                                    onChange={() => toggleLeadType(t.id)}
                                 />
                                 {t.label}
                              </label>
                           ))}
                        </div>
                        <div className="flex items-center gap-2">
                           {KINDS.map((k) => {
                              const on = draft.campaignType[0] === k.id
                              return (
                                 <button
                                    key={k.id}
                                    type="button"
                                    title={k.hint}
                                    onClick={() => setDraft({ ...draft, campaignType: [k.id] })}
                                    className={cn(
                                       'rounded-lg border px-3 py-1.5 text-sm transition-all',
                                       on
                                          ? 'border-[#9F7BFF] bg-[rgba(159,123,255,0.30)] text-white'
                                          : 'border-white/20 bg-white/5 text-white/70 hover:border-[#9F7BFF]/50'
                                    )}
                                 >
                                    {k.label}
                                 </button>
                              )
                           })}
                        </div>
                     </div>
                  </div>
               )
            )}

            {draft && (
               <Button
                  className="w-full"
                  onClick={pickDraft}
                  disabled={!draftReady}
                  data-testid="call-these-leads"
               >
                  {draftCount === 0
                     ? 'No leads match, loosen a filter'
                     : draft.nothingMatched && !advanced
                       ? 'Use names from Follow Up Boss'
                       : 'Call these leads'}{' '}
                  <ChevronRightIcon width={18} />
               </Button>
            )}
         </ContentCard>

         <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
            className="space-y-2"
         >
            <Grid>
               <Grid.Col span={3}>
                  <Button
                     variant="secondary"
                     className="w-full"
                     onClick={() => getPreviousStep()}
                  >
                     <ArrowLeftMdIcon width={12} /> Back
                  </Button>
               </Grid.Col>
               <Grid.Col span={9}>
                  <Button
                     className="w-full"
                     variant="secondary"
                     onClick={() => getNextStep()}
                     disabled={!saved?.campaign_name && !freeFlow}
                  >
                     {saved?.campaign_name
                        ? `Keep "${saved.campaign_name}"`
                        : 'Tap a campaign above to continue'}{' '}
                     <ChevronRightIcon width={18} />
                  </Button>
               </Grid.Col>
            </Grid>
            <p className="text-center text-xs text-white/40">
               Next: the voice your leads hear. Everything here can be changed later under Campaigns.
            </p>
         </motion.div>
      </Stack>
   )
}
